import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { Student } from '@/types';
import { formatDate } from '@/lib/datetime';

interface EndedStudentRowProps {
  student: Student;
  busy?: boolean;
  deleting?: boolean;
  onReactivate: (id: string) => void;
  onDelete: (id: string) => void;
}

export default function EndedStudentRow({
  student,
  busy,
  deleting,
  onReactivate,
  onDelete,
}: EndedStudentRowProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const initial = (student.name || '?').trim().charAt(0).toUpperCase();

  return (
    <div className="rounded-xl border border-background-200 bg-background-100 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-background-200 text-sm font-bold text-foreground-600">
            {initial}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Link
                to={`/students/${student.id}`}
                className="truncate text-sm font-semibold text-foreground-900 transition hover:text-primary-700"
              >
                {student.name}
              </Link>
              <span className="flex items-center gap-1 rounded-full bg-background-200 px-2 py-0.5 text-[11px] font-semibold text-foreground-600">
                <i className="ri-user-unfollow-line"></i>
                수업 종료
              </span>
            </div>
            <p className="mt-0.5 truncate text-xs text-foreground-500">
              {student.school || '학교 미입력'} · {student.grade || '학년 미입력'}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs text-foreground-500">종료일 {formatDate(student.ended_at)}</span>
          <button
            type="button"
            onClick={() => onReactivate(student.id)}
            disabled={busy || deleting}
            className="flex items-center gap-1.5 rounded-md border border-primary-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-primary-700 transition hover:bg-primary-50 disabled:opacity-50 whitespace-nowrap"
          >
            {busy ? (
              <i className="ri-loader-4-line animate-spin"></i>
            ) : (
              <i className="ri-restart-line"></i>
            )}
            수업 재개
          </button>
          <button
            type="button"
            onClick={() => setConfirmOpen((v) => !v)}
            disabled={busy || deleting}
            className="flex items-center gap-1.5 rounded-md border border-accent-200 bg-background-50 px-3 py-1.5 text-xs font-semibold text-accent-700 transition hover:bg-accent-50 disabled:opacity-50 whitespace-nowrap"
          >
            <i className="ri-delete-bin-6-line"></i>
            영구 삭제
          </button>
        </div>
      </div>

      {confirmOpen && (
        <div className="mt-3 rounded-lg border border-accent-300 bg-accent-50 p-3.5">
          <p className="flex items-center gap-1.5 text-xs font-bold text-accent-900">
            <i className="ri-error-warning-line"></i>
            {student.name} 학생을 완전히 삭제할까요?
          </p>
          <p className="mt-1 text-xs leading-relaxed text-accent-800">
            수업일지·진도·시험 성적·교습비·보충·학부모 공유 링크까지 <strong>모든 기록이 영구히
            삭제</strong>되며 되돌릴 수 없어요.
          </p>
          <div className="mt-2.5 flex gap-2">
            <button
              type="button"
              onClick={() => onDelete(student.id)}
              disabled={deleting}
              className="flex items-center gap-1 rounded-md bg-accent-500 px-3 py-1.5 text-xs font-semibold text-background-50 transition hover:bg-accent-600 disabled:opacity-50 whitespace-nowrap"
            >
              {deleting ? (
                <i className="ri-loader-4-line animate-spin"></i>
              ) : (
                <i className="ri-delete-bin-6-line"></i>
              )}
              영구 삭제
            </button>
            <button
              type="button"
              onClick={() => setConfirmOpen(false)}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-semibold text-foreground-700 transition hover:bg-background-100 whitespace-nowrap"
            >
              취소
            </button>
          </div>
        </div>
      )}
    </div>
  );
}